/**
 * RebuildWizardDialog.jsx
 * Shows during "Commit & Rebuild" — a live grid of every chapter
 * with its current drafting status (queued / running / done / error).
 *
 * Props
 *   open        boolean  — whether the dialog is visible
 *   chapters    array    — from project store (id, number, title)
 *   buildJob    object   — live job status from novelBuildApi.status()
 *                          { status, current_chapter, completed: number[],
 *                            total_chapters, chapter_words }
 *   buildProgress number — 0-100 for the top progress bar
 *   onClose     fn       — called when user dismisses (build keeps running)
 */
import React from 'react';
import { CheckCircle2, Loader2, Circle, XCircle, BookOpen } from 'lucide-react';
import { cn } from '@/lib/utils';

function ChapterTile({ number, title, chipStatus }) {
  const base = 'flex flex-col items-center justify-center rounded-lg border p-2 gap-1 text-center transition-all duration-300';
  const styles = {
    queued:    'border-border bg-muted/30 text-muted-foreground',
    running:   'border-primary/60 bg-primary/5 text-primary shadow-sm',
    completed: 'border-emerald-300 bg-emerald-50/50 text-emerald-700',
    error:     'border-destructive/40 bg-destructive/5 text-destructive',
  };
  const Icon = {
    queued:    Circle,
    running:   Loader2,
    completed: CheckCircle2,
    error:     XCircle,
  }[chipStatus] || Circle;

  return (
    <div className={cn(base, styles[chipStatus] || styles.queued)} data-testid={`rebuild-ch-${number}`}>
      <Icon className={cn('h-4 w-4', chipStatus === 'running' && 'animate-spin')} />
      <span className="text-[10px] font-semibold">Ch {number}</span>
      {title && (
        <span className="w-full truncate text-[9.5px] opacity-70 leading-tight px-0.5">
          {title.length > 18 ? title.slice(0, 16) + '…' : title}
        </span>
      )}
    </div>
  );
}

export default function RebuildWizardDialog({ open, chapters, buildJob, buildProgress, onClose }) {
  if (!open) return null;

  const completedNums = new Set(buildJob?.completed || []);
  const currentNum    = buildJob?.current_chapter;
  const jobStatus     = buildJob?.status || 'running';
  const totalChapters = buildJob?.total_chapters || chapters?.length || 0;
  const doneCount     = completedNums.size;
  const chapterWords  = buildJob?.chapter_words || {};

  const statusLabel = {
    queued:    'Queued',
    running:   `Drafting chapter ${currentNum || '…'} of ${totalChapters}`,
    paused:    'Paused — waiting for resume',
    done:      'All chapters drafted',
    error:     'Build encountered an error',
    cancelled: 'Build cancelled',
  }[jobStatus] || 'Building…';

  const chipFor = (chNum) => {
    if (completedNums.has(chNum)) return 'completed';
    if (chNum === currentNum)     return 'running';
    return 'queued';
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div
        className="relative mx-4 w-full max-w-xl rounded-xl border border-border bg-background shadow-xl"
        data-testid="rebuild-wizard-dialog"
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border px-5 py-4">
          <div className="flex items-center gap-2.5">
            <BookOpen className="h-4.5 w-4.5 text-primary" />
            <span className="text-[14px] font-semibold">Drafting Novel</span>
          </div>
          <button
            onClick={onClose}
            className="text-[11px] text-muted-foreground hover:text-foreground"
            data-testid="rebuild-wizard-close"
          >
            Minimise
          </button>
        </div>

        {/* Progress bar */}
        <div className="h-1.5 w-full bg-muted">
          <div
            className="h-full bg-primary transition-all duration-700"
            style={{ width: `${buildProgress}%` }}
          />
        </div>

        {/* Status line */}
        <div className="flex items-center gap-2 px-5 py-3 text-[12px] text-muted-foreground">
          {['running', 'queued'].includes(jobStatus) && (
            <Loader2 className="h-3.5 w-3.5 animate-spin text-primary flex-shrink-0" />
          )}
          {jobStatus === 'done' && <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 flex-shrink-0" />}
          {jobStatus === 'error' && <XCircle className="h-3.5 w-3.5 text-destructive flex-shrink-0" />}
          <span>{statusLabel}</span>
          <span className="ml-auto text-[11px]">
            {doneCount} / {totalChapters} done
          </span>
        </div>

        {/* Chapter grid */}
        <div className="max-h-72 overflow-y-auto px-5 pb-4">
          {chapters && chapters.length > 0 ? (
            <div className="grid grid-cols-4 gap-2 sm:grid-cols-5">
              {chapters.map((ch) => (
                <ChapterTile
                  key={ch.id || ch.number}
                  number={ch.number}
                  title={ch.title}
                  chipStatus={chipFor(ch.number)}
                />
              ))}
            </div>
          ) : (
            /* No chapter list yet — show numeric placeholders */
            <div className="grid grid-cols-5 gap-2">
              {Array.from({ length: totalChapters || 10 }, (_, i) => i + 1).map((n) => (
                <ChapterTile
                  key={n}
                  number={n}
                  title=""
                  chipStatus={chipFor(n)}
                />
              ))}
            </div>
          )}
        </div>

        {/* Word-count summary (post-build) */}
        {jobStatus === 'done' && Object.keys(chapterWords).length > 0 && (
          <div className="border-t border-border px-5 py-3 text-[11px] text-muted-foreground">
            Total drafted:{' '}
            <span className="font-semibold text-foreground">
              {Object.values(chapterWords).reduce((a, b) => a + b, 0).toLocaleString()} words
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
